// client/src/coms/personalisation/mobileLayout.js
// Mobile layout: presets, navigation destinations, and how the mobile prefs shape the layout on phones.
// Mobile prefs (prefs.mobile) are separate from the desktop layout (prefs.layout): on a phone-sized screen the
// app reads the mobile values for density, chat density, text size, conversation tabs and the pinned banner, and
// nothing on a phone writes the desktop layout (or the other way round).
import { DEFAULT_PREFS, MOBILE_NAV_LOCKED } from './prefsSchema';

export const MOBILE_NAV_OPTIONS = [
  { id: 'chats', label: 'Chats', icon: '🗨️', hint: 'Recent conversations from every server and DM' },
  { id: 'servers', label: 'Servers', icon: '🏠', hint: 'Servers and their channels' },
  { id: 'dms', label: 'DMs', icon: '✉️', hint: 'Direct messages, friends and requests' },
  { id: 'activities', label: 'Activities', icon: '🚀', hint: 'Games, Watch Together and more' },
  { id: 'chat', label: 'Last channel', icon: '💬', hint: 'Back to the last channel you had open' },
  { id: 'watch', label: 'Watch', icon: '▶️', hint: 'Watch Together' },
  { id: 'canvas', label: 'Canvas', icon: '🎨', hint: 'Shared drawing canvases' },
  { id: 'studio', label: 'Studio', icon: '🎚️', hint: 'Audio studio' },
  { id: 'maps', label: 'Maps', icon: '🗺️', hint: 'Interactive maps' },
  { id: 'voice', label: 'Voice', icon: '🔊', hint: 'Your voice call, or the voice channels' },
  { id: 'files', label: 'Files', icon: '📁', hint: 'Files and resources' },
  { id: 'search', label: 'Search', icon: '🔎', hint: 'Search messages and files' },
  { id: 'members', label: 'Members', icon: '🧑', hint: 'Who is in this server' },
  { id: 'marketplace', label: 'Market', icon: '🛍️', hint: 'Sticker and sound packs' },
  { id: 'profile', label: 'Profile', icon: '👤', hint: 'Your account and avatar' },
  { id: 'settings', label: 'Settings', icon: '⚙️', hint: 'Personal Settings' },
  { id: 'more', label: 'More', icon: '☰', hint: 'Everything else' },
];

export const MOBILE_NAV_BY_ID = Object.fromEntries(MOBILE_NAV_OPTIONS.map((o) => [o.id, o]));
// A bar with "More" for the rest shows at most this many buttons (More included); a scrolling bar shows them all.
export const MOBILE_BAR_MAX = 5;
export const MOBILE_NAV_MAX = 12;
export const MOBILE_NAV_MIN = 2;
export { MOBILE_NAV_LOCKED };

export const MOBILE_PRESET_INFO = [
  { id: 'classic', label: 'Classic', description: 'Bottom bar with icons and labels, a full header with the channel and server, comfortable spacing.' },
  { id: 'compact', label: 'Compact', description: 'Slim header, small icon-only bar, condensed messages, no tabs or banner: the most room for the conversation.' },
  { id: 'top', label: 'Top navigation', description: 'Navigation as a scrolling row of tabs under the header; nothing at the bottom but the message box.' },
  { id: 'bottom', label: 'Bottom navigation', description: 'Large one-handed bottom bar that hides while you scroll and returns when you scroll back; slim header.' },
  { id: 'dropdown', label: 'Dropdown', description: 'No bar at all: tap the conversation name to open one menu with every destination and your recent chats.' },
  { id: 'gesture', label: 'Gestures', description: 'Minimal controls: swipe up for navigation, down for conversations, right for servers; a small handle as a tap alternative.' },
  { id: 'custom', label: 'Custom', description: 'Your own combination of position, appearance, behaviour and destinations.' },
];

const DEFAULT_GESTURES = { swipeRight: 'servers', swipeLeft: 'members', swipeUp: 'none', swipeDown: 'none' };

// What each preset sets. Text size, the gesture on/off switch and saved layouts are personal and never part of one.
export const MOBILE_PRESET_STRUCTURE = {
  classic: {
    nav: ['chats', 'servers', 'dms', 'activities', 'more'],
    navPosition: 'bottom',
    navStyle: 'icons-labels',
    navBehaviour: 'always',
    navSize: 'regular',
    navOverflow: 'more',
    headerSize: 'regular',
    voiceDock: 'always',
    tabBar: 'auto',
    headerBar: true,
    density: 'comfortable',
    chatDensity: 'cozy',
    gestures: DEFAULT_GESTURES,
  },
  compact: {
    nav: ['chats', 'servers', 'dms', 'activities', 'more'],
    navPosition: 'bottom',
    navStyle: 'icons',
    navBehaviour: 'always',
    navSize: 'compact',
    navOverflow: 'more',
    headerSize: 'slim',
    voiceDock: 'always',
    tabBar: 'never',
    headerBar: false,
    density: 'compact',
    chatDensity: 'compact',
    gestures: DEFAULT_GESTURES,
  },
  top: {
    nav: ['chats', 'dms', 'servers', 'activities', 'canvas', 'studio', 'maps', 'more'],
    navPosition: 'top',
    navStyle: 'labels',
    navBehaviour: 'always',
    navSize: 'regular',
    navOverflow: 'scroll',
    headerSize: 'regular',
    voiceDock: 'always',
    tabBar: 'never',
    headerBar: true,
    density: 'comfortable',
    chatDensity: 'cozy',
    gestures: DEFAULT_GESTURES,
  },
  bottom: {
    nav: ['chats', 'dms', 'servers', 'activities', 'more'],
    navPosition: 'bottom',
    navStyle: 'emoji-labels',
    navBehaviour: 'autohide',
    navSize: 'large',
    navOverflow: 'more',
    headerSize: 'slim',
    voiceDock: 'always',
    tabBar: 'never',
    headerBar: false,
    density: 'comfortable',
    chatDensity: 'cozy',
    gestures: DEFAULT_GESTURES,
  },
  dropdown: {
    nav: ['chats', 'dms', 'servers', 'activities', 'canvas', 'studio', 'maps', 'marketplace', 'more'],
    navPosition: 'dropdown-top',
    navStyle: 'icons-labels',
    navBehaviour: 'always',
    navSize: 'regular',
    navOverflow: 'more',
    headerSize: 'regular',
    voiceDock: 'always',
    tabBar: 'never',
    headerBar: true,
    density: 'comfortable',
    chatDensity: 'cozy',
    gestures: DEFAULT_GESTURES,
  },
  gesture: {
    nav: ['chats', 'dms', 'servers', 'activities', 'canvas', 'studio', 'maps', 'more'],
    navPosition: 'gesture',
    navStyle: 'icons-labels',
    navBehaviour: 'always',
    navSize: 'regular',
    navOverflow: 'more',
    headerSize: 'slim',
    voiceDock: 'always',
    tabBar: 'never',
    headerBar: false,
    density: 'compact',
    chatDensity: 'cozy',
    gestures: { swipeRight: 'servers', swipeLeft: 'chats', swipeUp: 'menu', swipeDown: 'switcher' },
  },
};

export const STRUCTURE_KEYS = Object.keys(MOBILE_PRESET_STRUCTURE.classic);

const copyValue = (v) => (Array.isArray(v) ? v.slice() : v && typeof v === 'object' ? { ...v } : v);

export function applyMobilePreset(mobile, presetId) {
  const s = MOBILE_PRESET_STRUCTURE[presetId];
  if (!s) return { ...mobile, preset: 'custom' };
  const next = { ...mobile, preset: presetId };
  for (const k of STRUCTURE_KEYS) next[k] = copyValue(s[k]);
  return next;
}

const same = (a, b) => {
  if (Array.isArray(a)) return Array.isArray(b) && a.length === b.length && a.every((x, i) => x === b[i]);
  if (a && typeof a === 'object') return !!b && typeof b === 'object' && Object.keys(a).every((k) => a[k] === b[k]);
  return a === b;
};

// The preset these values match exactly, or 'custom'.
export function matchPreset(mobile) {
  const match = Object.entries(MOBILE_PRESET_STRUCTURE).find(([, s]) => STRUCTURE_KEYS.every((k) => same(s[k], mobile[k])));
  return match ? match[0] : 'custom';
}

// Changing a structural value turns the layout into Custom, unless the result matches a preset exactly.
export function setMobileValue(mobile, key, value) {
  const next = { ...mobile, [key]: copyValue(value) };
  if (!STRUCTURE_KEYS.includes(key)) return next;
  return { ...next, preset: matchPreset(next) };
}

// Several values at once (the layout editor's Apply, or a saved layout).
export function withMobileValues(mobile, values) {
  const next = { ...mobile };
  for (const [k, v] of Object.entries(values || {})) next[k] = copyValue(v);
  return { ...next, preset: matchPreset(next) };
}

export function layoutValuesOf(mobile) {
  const out = {};
  for (const k of STRUCTURE_KEYS) out[k] = copyValue(mobile[k]);
  return out;
}

export function defaultMobile() {
  const d = DEFAULT_PREFS.mobile;
  return { ...d, nav: d.nav.slice(), gestures: { ...d.gestures }, saved: [] };
}

// The effective layout on a phone: only the values a phone shows come from the mobile prefs.
export function layoutForMobile(layout, mobile) {
  const m = mobile || DEFAULT_PREFS.mobile;
  return {
    ...layout,
    density: m.density,
    chatDensity: m.chatDensity,
    messageScale: m.messageScale,
    tabBar: m.tabBar,
    headerBar: m.headerBar,
    uiScale: 1,
  };
}

// ---------- navigation helpers (pure; used by the bars, menus and Settings) ----------
export function moveNavItem(nav, id, delta) {
  const i = nav.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= nav.length) return nav;
  const out = nav.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

export function toggleNavItem(nav, id) {
  if (MOBILE_NAV_LOCKED.includes(id)) return nav;
  if (nav.includes(id)) return nav.length > MOBILE_NAV_MIN ? nav.filter((x) => x !== id) : nav;
  if (nav.length >= MOBILE_NAV_MAX) return nav;
  // New items go before More, which stays the last-resort entry unless the user moved it.
  const moreAt = nav.indexOf('more');
  const out = nav.slice();
  out.splice(moreAt === nav.length - 1 ? moreAt : out.length, 0, id);
  return out;
}

// The buttons a bar shows: everything when it scrolls; otherwise the first destinations that fit plus More.
export function barItems(nav, overflow = 'more', max = MOBILE_BAR_MAX) {
  if (overflow === 'scroll') return nav.slice();
  const items = nav.filter((id) => id !== 'more');
  if (items.length < max) return nav.slice();
  return [...items.slice(0, max - 1), 'more'];
}

// Destinations listed under More: everything not shown in the bar.
export function moreDestinations(shown) {
  return MOBILE_NAV_OPTIONS.filter((o) => o.id !== 'more' && !shown.includes(o.id));
}

// Destinations a menu (Dropdown, Gestures) lists: the user's own first, in their order, then everything else.
export function menuDestinations(nav) {
  const mine = nav.filter((id) => id !== 'more').map((id) => MOBILE_NAV_BY_ID[id]).filter(Boolean);
  return { mine, rest: moreDestinations(nav) };
}

// The navigation pieces a layout puts on screen.
export function layoutParts(mobile) {
  const p = mobile.navPosition;
  return {
    bottomBar: p === 'bottom',
    topBar: p === 'top',
    menuFromHeader: p === 'dropdown-top',
    bottomLauncher: p === 'dropdown-bottom',
    handle: p === 'gesture',
  };
}
